import React, { useState, useEffect, useCallback } from 'react';
import { KnowledgeBase, CreateKnowledgeBaseRequest, UpdateKnowledgeBaseRequest } from '@shared/types';
import { knowledgeBaseAPI } from '../services/api';
import { KnowledgeBaseForm } from '../components/knowledge-base/KnowledgeBaseForm';
import { KnowledgeBaseList } from '../components/knowledge-base/KnowledgeBaseList';
import DocumentViewer from '../components/knowledge-base/DocumentViewer';
import { useNavigate } from 'react-router-dom';

const KnowledgeBaseManagement: React.FC = () => {
    const navigate = useNavigate();
    const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>('');
    const [showForm, setShowForm] = useState<boolean>(false);
    const [editingKnowledgeBase, setEditingKnowledgeBase] = useState<KnowledgeBase | null>(null);
    const [formLoading, setFormLoading] = useState<boolean>(false);
    const [viewingDocuments, setViewingDocuments] = useState<KnowledgeBase | null>(null);

    // 加载知识库列表
    const loadKnowledgeBases = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const result = await knowledgeBaseAPI.getKnowledgeBases();
            if (result.success && result.data) {
                setKnowledgeBases(result.data);
            } else {
                setError(result.error || '加载知识库失败');
            }
        } catch (error) {
            console.error('加载知识库失败:', error);
            setError('加载知识库失败，请稍后再试');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadKnowledgeBases();
    }, [loadKnowledgeBases]);

    // 创建知识库
    const handleCreateKnowledgeBase = async (data: CreateKnowledgeBaseRequest) => {
        try {
            setFormLoading(true);
            const result = await knowledgeBaseAPI.createKnowledgeBase(data);
            if (result.success && result.data) {
                setKnowledgeBases(prev => [...prev, result.data!]);
                setShowForm(false);
                // 显示成功消息
                alert('知识库创建成功！');
            } else {
                alert(result.error || '创建失败');
            }
        } catch (error) {
            console.error('创建知识库失败:', error);
            alert('创建失败，请稍后再试');
        } finally {
            setFormLoading(false);
        }
    };

    // 更新知识库
    const handleUpdateKnowledgeBase = async (data: UpdateKnowledgeBaseRequest) => {
        if (!editingKnowledgeBase) return;

        try {
            setFormLoading(true);
            const result = await knowledgeBaseAPI.updateKnowledgeBase(editingKnowledgeBase.id, data);
            if (result.success && result.data) {
                setKnowledgeBases(prev =>
                    prev.map(kb => kb.id === editingKnowledgeBase.id ? result.data! : kb)
                );
                setEditingKnowledgeBase(null);
                // 显示成功消息
                alert('知识库更新成功！');
            } else {
                alert(result.error || '更新失败');
            }
        } catch (error) {
            console.error('更新知识库失败:', error);
            alert('更新失败，请稍后再试');
        } finally {
            setFormLoading(false);
        }
    };

    // 删除知识库
    const handleDeleteKnowledgeBase = async (id: string) => {
        if (!confirm('确定要删除这个知识库吗？删除后无法恢复。')) {
            return;
        }

        try {
            const result = await knowledgeBaseAPI.deleteKnowledgeBase(id);
            if (result.success) {
                setKnowledgeBases(prev => prev.filter(kb => kb.id !== id));
                alert('知识库删除成功！');
            } else {
                alert(result.error || '删除失败');
            }
        } catch (error) {
            console.error('删除知识库失败:', error);
            alert('删除失败，请稍后再试');
        }
    };

    // 编辑知识库
    const handleEditKnowledgeBase = (knowledgeBase: KnowledgeBase) => {
        setEditingKnowledgeBase(knowledgeBase);
        setShowForm(true);
    };

    // 查看知识库
    const handleViewKnowledgeBase = (id: string) => {
        navigate(`/knowledge-base/${id}`);
    };

    // 查看知识库文档
    const handleViewDocuments = (knowledgeBase: KnowledgeBase) => {
        setViewingDocuments(knowledgeBase);
    };

    // 关闭文档查看器
    const handleCloseDocumentViewer = () => {
        setViewingDocuments(null);
    };

    // 取消表单
    const handleCancelForm = () => {
        setShowForm(false);
        setEditingKnowledgeBase(null);
    };

    // 提交表单
    const handleSubmitForm = (data: CreateKnowledgeBaseRequest | UpdateKnowledgeBaseRequest) => {
        if (editingKnowledgeBase) {
            handleUpdateKnowledgeBase(data as UpdateKnowledgeBaseRequest);
        } else {
            handleCreateKnowledgeBase(data as CreateKnowledgeBaseRequest);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* 页面头部 */}
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">知识库管理</h1>
                            <p className="mt-2 text-gray-600">
                                创建、管理和组织您的学习知识库
                            </p>
                        </div>
                        <div className="flex space-x-4">
                            <button
                                onClick={() => navigate('/')}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                ← 返回首页
                            </button>
                            <button
                                onClick={() => setShowForm(true)}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                + 创建知识库
                            </button>
                        </div>
                    </div>
                </div>

                {/* 统计信息 */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    <div className="bg-white rounded-lg shadow-sm border p-6">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                    <span className="text-blue-600 text-lg">📚</span>
                                </div>
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-500">总知识库</p>
                                <p className="text-2xl font-semibold text-gray-900">{knowledgeBases.length}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm border p-6">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                    <span className="text-green-600 text-lg">✅</span>
                                </div>
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-500">活跃知识库</p>
                                <p className="text-2xl font-semibold text-gray-900">
                                    {knowledgeBases.filter(kb => kb.status === 'active').length}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm border p-6">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                                    <span className="text-yellow-600 text-lg">📊</span>
                                </div>
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-500">草稿知识库</p>
                                <p className="text-2xl font-semibold text-gray-900">
                                    {knowledgeBases.filter(kb => kb.status === 'draft').length}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm border p-6">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                                    <span className="text-purple-600 text-lg">🏷️</span>
                                </div>
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-500">分类数量</p>
                                <p className="text-2xl font-semibold text-gray-900">
                                    {new Set(knowledgeBases.map(kb => kb.category)).size}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 主要内容区域 */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* 左侧：知识库列表 */}
                    <div className="lg:col-span-2">
                        {loading ? (
                            <div className="bg-white rounded-lg shadow-sm border p-8">
                                <div className="flex justify-center items-center">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                    <span className="ml-2 text-gray-600">加载中...</span>
                                </div>
                            </div>
                        ) : error ? (
                            <div className="bg-white rounded-lg shadow-sm border p-8">
                                <div className="text-center">
                                    <div className="text-red-500 text-4xl mb-4">❌</div>
                                    <h3 className="text-lg font-medium text-gray-900 mb-2">加载失败</h3>
                                    <p className="text-gray-600 mb-4">{error}</p>
                                    <button
                                        onClick={loadKnowledgeBases}
                                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                                    >
                                        重试
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <KnowledgeBaseList
                                knowledgeBases={knowledgeBases}
                                onEdit={handleEditKnowledgeBase}
                                onDelete={handleDeleteKnowledgeBase}
                                onView={handleViewKnowledgeBase}
                                onViewDocuments={handleViewDocuments}
                                loading={loading}
                            />
                        )}
                    </div>

                    {/* 右侧：创建/编辑表单 */}
                    <div className="lg:col-span-1">
                        {showForm && (
                            <div className="sticky top-8">
                                <KnowledgeBaseForm
                                    knowledgeBase={editingKnowledgeBase || undefined}
                                    onSubmit={handleSubmitForm}
                                    onCancel={handleCancelForm}
                                    loading={formLoading}
                                />
                            </div>
                        )}

                        {!showForm && (
                            <div className="bg-white rounded-lg shadow-sm border p-6">
                                <div className="text-center">
                                    <div className="text-gray-400 text-4xl mb-4">📚</div>
                                    <h3 className="text-lg font-medium text-gray-900 mb-2">创建知识库</h3>
                                    <p className="text-gray-600 mb-4">
                                        点击上方按钮开始创建您的第一个知识库
                                    </p>
                                    <button
                                        onClick={() => setShowForm(true)}
                                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                                    >
                                        开始创建
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* 文档查看器 */}
            {viewingDocuments && (
                <DocumentViewer
                    knowledgeBaseId={viewingDocuments.id}
                    knowledgeBaseName={viewingDocuments.name}
                    onClose={handleCloseDocumentViewer}
                />
            )}
        </div>
    );
};

export default KnowledgeBaseManagement;
